import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import {
  ShoppingBag,
  Shield,
  Truck,
  RefreshCw,
  Award,
  Star,
  Check,
  Search,
  Heart,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  Languages,
  Linkedin
} from 'lucide-react';
import { testimonials, howItWorks, trustBadges, stats } from '../mock';
import ContactModal from '../components/ContactModal';
import { useLanguage } from '../LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const { language, toggleLanguage, t } = useLanguage();
  const [email, setEmail] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [randomProducts, setRandomProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  // Fetch random products from API
  useEffect(() => {
    const fetchRandomProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        const allProducts = response.data;
        
        // Filter products with images
        const productsWithImages = allProducts.filter(p => p.images && p.images.length > 0);
        
        // Shuffle and pick 4 random products
        const shuffled = [...productsWithImages].sort(() => Math.random() - 0.5);
        const selected = shuffled.slice(0, 4).map(p => ({
          id: p.id,
          name: p.title,
          category: p.category,
          price: p.price,
          condition: p.condition,
          description: p.description,
          image: `${BACKEND_URL}/api/uploads/products/${p.images[0]}/thumbnail`,
          fullImage: `${BACKEND_URL}/api/uploads/products/${p.images[0]}`,
          images: p.images.map(img => `${BACKEND_URL}/api/uploads/products/${img}`)
        }));
        
        setRandomProducts(selected);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setProductsLoading(false);
      }
    };

    fetchRandomProducts();
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/newsletter`, { email });
      alert(t.newsletter.success);
      setEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      if (error.response?.data?.detail?.includes('already exists')) {
        alert(t.newsletter.alreadySubscribed);
      } else {
        alert(t.newsletter.success);
      }
      setEmail('');
    }
  };

  const handleAddToWishlist = (productId) => {
    console.log('Added to wishlist:', productId);
    alert(`${t.wishlist.addedToWishlist} ${t.wishlist.mockMessage}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/collection?search=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate('/collection');
    }
  };

  const iconMap = {
    Shield: Shield,
    Truck: Truck,
    RefreshCw: RefreshCw,
    Award: Award
  };

  return (
    <div className="landing-page">
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <ShoppingBag className="w-8 h-8" style={{ color: 'var(--lv-brown)' }} />
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}>
                Love Luxe
              </h1>
              <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--warm-gray)', letterSpacing: '1.5px' }}>
                Second-Hand Luxury
              </p>
            </div>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <a href="#collection" className="text-sm uppercase tracking-wide hover:text-[var(--lv-brown)] transition-colors" style={{ color: 'var(--charcoal)', letterSpacing: '1px' }}>
              {t.nav.collection}
            </a>
            <button onClick={() => navigate('/blog')} className="text-sm uppercase tracking-wide hover:text-[var(--lv-brown)] transition-colors" style={{ color: 'var(--charcoal)', letterSpacing: '1px' }}>
              Blog
            </button>
            <a href="#how-it-works" className="text-sm uppercase tracking-wide hover:text-[var(--lv-brown)] transition-colors" style={{ color: 'var(--charcoal)', letterSpacing: '1px' }}>
              {t.nav.howItWorks}
            </a>
            <a href="#about" className="text-sm uppercase tracking-wide hover:text-[var(--lv-brown)] transition-colors" style={{ color: 'var(--charcoal)', letterSpacing: '1px' }}>
              {t.nav.about}
            </a>
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              style={{ color: 'var(--charcoal)' }}
              title={language === 'de' ? 'Switch to English' : 'Zu Deutsch wechseln'}
            >
              <Languages className="w-5 h-5" />
              <span className="text-sm font-medium uppercase">{language === 'de' ? 'EN' : 'DE'}</span>
            </button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ 
          background: 'linear-gradient(135deg, var(--cream-white) 0%, var(--soft-gray) 50%, var(--soft-taupe) 100%)',
          marginTop: '80px'
        }}
      >
        <div className="absolute inset-0 opacity-10" style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1758633854736-8973bcd84dd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxkZXNpZ25lciUyMGJvdXRpcXVlfGVufDB8fHx8MTc2MDA0NzQwOHww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }} />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 text-center animate-fade-in-up">
          <Badge className="mb-6 uppercase tracking-widest" style={{ 
            backgroundColor: 'var(--warm-sage)',
            color: 'var(--cream-white)',
            letterSpacing: '2px',
            fontSize: '12px',
            padding: '8px 20px'
          }}>
            {t.hero.badge}
          </Badge>
          
          <h1 
            className="text-5xl md:text-7xl font-light mb-6 leading-tight"
            style={{ 
              fontFamily: 'Playfair Display, serif',
              color: 'var(--charcoal)',
              letterSpacing: '-1px'
            }}
          >
            {t.hero.title}
            <br />
            <span style={{ color: 'var(--lv-brown)' }}>{t.hero.subtitle}</span>
          </h1>
          
          <p 
            className="text-lg md:text-xl mb-10 max-w-2xl mx-auto"
            style={{ color: 'var(--warm-gray)', lineHeight: '1.8' }}
          >
            {t.hero.description}
            <br />{t.hero.descriptionLine2}
            <br />{t.hero.descriptionLine3}
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-xl mx-auto mb-10">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: 'var(--warm-gray)' }} />
              <Input
                type="text"
                placeholder={t.hero.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-6 text-base"
                style={{ 
                  backgroundColor: 'white',
                  border: '1px solid var(--soft-taupe)',
                  borderRadius: '8px'
                }}
              />
            </div>
          </form>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg"
              className="uppercase tracking-wider text-base"
              onClick={() => navigate('/collection')}
              style={{ 
                backgroundColor: 'var(--rich-chocolate)', 
                color: 'var(--cream-white)',
                letterSpacing: '1.5px',
                padding: '24px 40px',
                height: 'auto',
                fontSize: '14px'
              }}
            >
              {t.hero.exploreCollection}
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
            
            <Button 
              variant="outline"
              size="lg"
              className="uppercase tracking-wider text-base"
              style={{ 
                borderColor: 'var(--charcoal)',
                color: 'var(--charcoal)',
                backgroundColor: 'transparent',
                letterSpacing: '1.5px',
                padding: '24px 40px',
                height: 'auto',
                fontSize: '14px'
              }}
            >
              {t.hero.learnMore}
            </Button>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-8 mt-16">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" style={{ color: 'var(--lv-brown)' }} />
              <span className="text-sm" style={{ color: 'var(--charcoal)' }}>{t.trustBadges.authenticated}</span>
            </div>
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5" style={{ color: 'var(--lv-brown)' }} />
              <span className="text-sm" style={{ color: 'var(--charcoal)' }}>{t.trustBadges.shipping}</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5" style={{ color: 'var(--lv-brown)' }} />
              <span className="text-sm" style={{ color: 'var(--charcoal)' }}>{t.trustBadges.curated}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16" style={{ backgroundColor: 'var(--soft-gray)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            <div className="text-center">
              <h3 
                className="text-4xl md:text-5xl font-light mb-2"
                style={{ fontFamily: 'Playfair Display, serif', color: 'var(--lv-brown)' }}
              >
                2000+
              </h3>
              <p className="text-sm uppercase tracking-wider" style={{ color: 'var(--warm-gray)', letterSpacing: '1.5px' }}>
                {t.stats.piecesSold}
              </p>
            </div>
            <a 
              href="https://www.ricardo.ch/de/shop/Luxury_Limited/ratings/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-center hover:opacity-80 transition-opacity cursor-pointer"
            >
              <h3 
                className="text-4xl md:text-5xl font-light mb-2"
                style={{ fontFamily: 'Playfair Display, serif', color: 'var(--lv-brown)' }}
              >
                99.7%
              </h3>
              <p className="text-sm uppercase tracking-wider" style={{ color: 'var(--warm-gray)', letterSpacing: '1.5px' }}>
                {t.stats.satisfactionRate}
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--lv-brown)' }}>
                {t.stats.seeOnRicardo}
              </p>
            </a>
            <div className="text-center">
              <h3 
                className="text-4xl md:text-5xl font-light mb-2"
                style={{ fontFamily: 'Playfair Display, serif', color: 'var(--lv-brown)' }}
              >
                100%
              </h3>
              <p className="text-sm uppercase tracking-wider" style={{ color: 'var(--warm-gray)', letterSpacing: '1.5px' }}>
                {t.stats.authenticated}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section id="collection" className="py-24" style={{ backgroundColor: 'var(--cream-white)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 
              className="text-4xl md:text-5xl font-light mb-4"
              style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}
            >
              {t.featuredCollection.title}
            </h2>
            <p className="text-lg" style={{ color: 'var(--warm-gray)' }}>
              {t.featuredCollection.subtitle}
              <br />
              {t.featuredCollection.subtitleLine2}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {productsLoading ? (
              // Loading skeleton
              [...Array(4)].map((_, i) => (
                <Card key={i} className="overflow-hidden border-0 animate-pulse" style={{ backgroundColor: 'white', boxShadow: '0 4px 16px rgba(0,0,0,0.08)', borderRadius: '8px' }}>
                  <div className="w-full h-80 bg-gray-200"></div>
                  <CardContent className="p-6">
                    <div className="h-3 bg-gray-200 rounded w-1/3 mb-2"></div>
                    <div className="h-5 bg-gray-200 rounded w-2/3 mb-3"></div>
                    <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                  </CardContent>
                </Card>
              ))
            ) : (
              randomProducts.map((product) => (
                <Card 
                  key={product.id} 
                  className="card group overflow-hidden border-0 cursor-pointer"
                  style={{ 
                    backgroundColor: 'white',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    borderRadius: '8px'
                  }}
                  onClick={() => navigate(`/collection?search=${encodeURIComponent(product.name)}`)}
                >
                  <div className="relative overflow-hidden">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => { e.target.src = product.fullImage; }}
                    />
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAddToWishlist(product.id); }}
                      className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-colors"
                    >
                      <Heart className="w-5 h-5" style={{ color: 'var(--warm-gray)' }} />
                    </button>
                    <Badge 
                      className="absolute top-4 left-4"
                      style={{ backgroundColor: 'var(--warm-sage)', color: 'var(--cream-white)' }}
                    >
                      {product.condition}
                    </Badge>
                  </div>
                  
                  <CardContent className="p-6">
                    <p className="text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--warm-gray)', letterSpacing: '1.5px' }}>
                      {product.category}
                    </p>
                    <h3 className="text-lg font-medium mb-3 line-clamp-2" style={{ color: 'var(--charcoal)' }}>
                      {product.name}
                    </h3>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-2xl font-semibold" style={{ color: 'var(--lv-brown)' }}>
                          CHF {product.price}
                        </p>
                      </div>
                      <Button 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProduct(product.name);
                          setIsContactModalOpen(true);
                        }}
                        style={{ backgroundColor: 'var(--warm-sage)', color: 'var(--cream-white)' }}
                      >
                        {t.product.contactUs}
                      </Button>
                    </div>
                    <Button 
                      size="sm"
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/payment?product=${encodeURIComponent(product.name)}&price=${product.price}`);
                      }}
                      style={{ backgroundColor: 'var(--lv-brown)', color: 'var(--cream-white)' }}
                    >
                      {language === 'de' ? 'Kaufen' : 'Buy Now'}
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg"
              className="uppercase tracking-wider"
              onClick={() => navigate('/collection')}
              style={{ 
                backgroundColor: 'var(--charcoal)',
                color: 'var(--cream-white)',
                letterSpacing: '1.5px',
                padding: '20px 48px'
              }}
            >
              {t.featuredCollection.viewAll}
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24" style={{ backgroundColor: 'var(--soft-gray)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-light mb-4"
              style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}
            >
              {t.howItWorks.title}
            </h2>
            <p className="text-lg" style={{ color: 'var(--warm-gray)' }}>
              {t.howItWorks.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center animate-fade-in-up">
              <div 
                className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--warm-sage)' }}
              >
                <span 
                  className="text-3xl font-light"
                  style={{ fontFamily: 'Playfair Display, serif', color: 'var(--cream-white)' }}
                >
                  01
                </span>
              </div>
              <h3 className="text-xl font-medium mb-3" style={{ color: 'var(--charcoal)' }}>
                {t.howItWorks.step1Title}
              </h3>
              <p style={{ color: 'var(--warm-gray)', lineHeight: '1.6' }}>
                {t.howItWorks.step1Desc}
              </p>
            </div>
            <div className="text-center animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div 
                className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--warm-sage)' }}
              >
                <span 
                  className="text-3xl font-light"
                  style={{ fontFamily: 'Playfair Display, serif', color: 'var(--cream-white)' }}
                >
                  02
                </span>
              </div>
              <h3 className="text-xl font-medium mb-3" style={{ color: 'var(--charcoal)' }}>
                {t.howItWorks.step2Title}
              </h3>
              <p style={{ color: 'var(--warm-gray)', lineHeight: '1.6' }}>
                {t.howItWorks.step2Desc}
              </p>
              <p style={{ color: 'var(--warm-sage)', lineHeight: '1.6', marginTop: '0.75rem', fontStyle: 'italic' }}>
                {t.howItWorks.step2Extra}
              </p>
            </div>
            <div className="text-center animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div 
                className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--warm-sage)' }}
              >
                <span 
                  className="text-3xl font-light"
                  style={{ fontFamily: 'Playfair Display, serif', color: 'var(--cream-white)' }}
                >
                  03
                </span>
              </div>
              <h3 className="text-xl font-medium mb-3" style={{ color: 'var(--charcoal)' }}>
                {t.howItWorks.step3Title}
              </h3>
              <p style={{ color: 'var(--warm-gray)', lineHeight: '1.6' }}>
                {t.howItWorks.step3Desc}
              </p>
              <p style={{ color: 'var(--warm-sage)', lineHeight: '1.6', marginTop: '0.75rem', fontStyle: 'italic' }}>
                {t.howItWorks.step3Extra}
              </p>
            </div>
            <div className="text-center animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <div 
                className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--warm-sage)' }}
              >
                <span 
                  className="text-3xl font-light"
                  style={{ fontFamily: 'Playfair Display, serif', color: 'var(--cream-white)' }}
                >
                  04
                </span>
              </div>
              <h3 className="text-xl font-medium mb-3" style={{ color: 'var(--charcoal)' }}>
                {t.howItWorks.step4Title}
              </h3>
              <p style={{ color: 'var(--warm-gray)', lineHeight: '1.6' }}>
                {t.howItWorks.step4Desc}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Authentication Guarantee Section */}
      <section id="about" className="py-24" style={{ backgroundColor: 'var(--cream-white)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1573612664822-d7d347da7b80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxkZXNpZ25lciUyMGJvdXRpcXVlfGVufDB8fHx8MTc2MDA0NzQwOHww&ixlib=rb-4.1.0&q=85"
                alt="Authentication Process"
                className="rounded-lg shadow-xl"
                style={{ borderRadius: '8px' }}
              />
              {/* Portrait */}
              <img 
                src="https://customer-assets.emergentagent.com/job_swapshop-lux/artifacts/h8n4yowo_Milla.jfif"
                alt="Milla - Gründerin"
                className="absolute bottom-4 left-4 w-20 h-20 rounded-full object-cover border-4 shadow-lg"
                style={{ borderColor: 'var(--cream-white)' }}
              />
              {/* Tagline under portrait */}
              <div 
                className="absolute bottom-4 left-28 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-md"
              >
                <p className="text-sm font-medium" style={{ color: 'var(--charcoal)', fontStyle: 'italic' }}>
                  Jedes Stück erzählt eine Geschichte.<br />
                  Nachhaltiger Luxus, zeitloser Stil.
                </p>
              </div>
            </div>
            <div>
              <Badge 
                className="mb-4 uppercase tracking-widest"
                style={{ 
                  backgroundColor: 'var(--rich-chocolate)',
                  color: 'var(--cream-white)',
                  letterSpacing: '2px'
                }}
              >
                {t.authentication.badge}
              </Badge>
              <h2 
                className="text-4xl md:text-5xl font-light mb-6"
                style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}
              >
                {t.authentication.title}
                <br />{t.authentication.titleLine2}
              </h2>
              <p className="text-lg mb-6" style={{ color: 'var(--warm-gray)', lineHeight: '1.8' }}>
                {t.authentication.description}
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--warm-sage)' }}
                  >
                    <Check className="w-4 h-4" style={{ color: 'var(--cream-white)' }} />
                  </div>
                  <p style={{ color: 'var(--charcoal)' }}>{t.authentication.check1}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--warm-sage)' }}
                  >
                    <Check className="w-4 h-4" style={{ color: 'var(--cream-white)' }} />
                  </div>
                  <p style={{ color: 'var(--charcoal)' }}>{t.authentication.check2}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--warm-sage)' }}
                  >
                    <Check className="w-4 h-4" style={{ color: 'var(--cream-white)' }} />
                  </div>
                  <p style={{ color: 'var(--charcoal)' }}>{t.authentication.check3}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'var(--warm-sage)' }}
                  >
                    <Check className="w-4 h-4" style={{ color: 'var(--cream-white)' }} />
                  </div>
                  <p style={{ color: 'var(--charcoal)' }}>{t.authentication.check4}</p>
                </div>
                <div className="flex items-start gap-3">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                    style={{ backgroundColor: 'var(--warm-sage)' }}
                  >
                    <Check className="w-4 h-4" style={{ color: 'var(--cream-white)' }} />
                  </div>
                  <div>
                    <p style={{ color: 'var(--charcoal)' }}>{t.authentication.check5}</p>
                    <p className="text-sm mt-1" style={{ color: 'var(--warm-gray)' }}>{t.authentication.check5Small}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Buy-back Section */}
      {/* Testimonials Section */}
      <section className="py-24" style={{ backgroundColor: 'var(--soft-gray)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-light mb-4"
              style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}
            >
              {t.testimonials.title}
            </h2>
            <p className="text-lg" style={{ color: 'var(--warm-gray)' }}>
              {t.testimonials.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card 
                key={testimonial.id}
                className="border-0"
                style={{ 
                  backgroundColor: 'white',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  borderRadius: '8px'
                }}
              >
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-current" style={{ color: 'var(--gold-accent)' }} />
                    ))}
                  </div>
                  <p className="mb-6" style={{ color: 'var(--charcoal)', lineHeight: '1.8' }}>
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center font-medium"
                      style={{ backgroundColor: 'var(--warm-sage)', color: 'var(--cream-white)' }}
                    >
                      {testimonial.avatar}
                    </div>
                    <div>
                      <p className="font-medium" style={{ color: 'var(--charcoal)' }}>
                        {testimonial.name}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--warm-gray)' }}>
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA Section */}
      <section className="py-24" style={{ backgroundColor: 'var(--rich-chocolate)' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Mail className="w-16 h-16 mx-auto mb-6" style={{ color: 'var(--cream-white)' }} />
          <h2 
            className="text-4xl md:text-5xl font-light mb-4"
            style={{ fontFamily: 'Playfair Display, serif', color: 'var(--cream-white)' }}
          >
            {t.newsletter.title}
          </h2>
          <p className="text-lg mb-8" style={{ color: 'var(--soft-taupe)' }}>
            {t.newsletter.subtitle}
          </p>
          
          <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto">
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder={t.newsletter.placeholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1"
                style={{ 
                  backgroundColor: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  height: '48px'
                }}
              />
              <Button 
                type="submit"
                size="lg"
                className="uppercase tracking-wider"
                style={{ 
                  backgroundColor: 'var(--warm-sage)',
                  color: 'var(--cream-white)',
                  letterSpacing: '1.5px',
                  height: '48px',
                  padding: '0 32px'
                }}
              >
                {t.newsletter.subscribe}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ backgroundColor: 'var(--charcoal)', color: 'var(--cream-white)' }}>
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <ShoppingBag className="w-8 h-8" style={{ color: 'var(--warm-sage)' }} />
                <div>
                  <h3 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Love Luxe
                  </h3>
                  <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--warm-gray)' }}>
                    Second-Hand Luxury
                  </p>
                </div>
              </div>
              <p className="text-sm" style={{ color: 'var(--warm-gray)', lineHeight: '1.6' }}>
                {t.footer.tagline}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ letterSpacing: '1.5px' }}>
                {t.footer.shop}
              </h4>
              <ul className="space-y-2 text-sm" style={{ color: 'var(--warm-gray)' }}>
                <li><a href="#" className="hover:text-white transition-colors">{t.footer.handbags}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t.footer.accessories}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t.footer.newArrivals}</a></li>
                <li><a href="#" className="hover:text-white transition-colors">{t.footer.bestSellers}</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ letterSpacing: '1.5px' }}>
                {t.footer.support}
              </h4>
              <ul className="space-y-2 text-sm" style={{ color: 'var(--warm-gray)' }}>
                <li><a href="#about" className="hover:text-white transition-colors">{t.footer.authentication}</a></li>
                <li>
                  <button 
                    onClick={() => navigate('/terms-of-service#lieferung-versand')} 
                    className="hover:text-white transition-colors text-left"
                  >
                    {t.footer.shippingReturns}
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => setIsContactModalOpen(true)} 
                    className="hover:text-white transition-colors text-left"
                  >
                    {t.footer.faq}
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => setIsContactModalOpen(true)} 
                    className="hover:text-white transition-colors text-left"
                  >
                    {t.footer.contactUs}
                  </button>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ letterSpacing: '1.5px' }}>
                {t.footer.contact}
              </h4>
              <ul className="space-y-3 text-sm" style={{ color: 'var(--warm-gray)' }}>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>contact@lvluxury.ch</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>+41 41 543 36 77</span>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <a 
                    href="https://maps.app.goo.gl/KM4hdMATanobbc8P7"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity hover:underline"
                  >
                    Steinhausen, ZG, Switzerland
                  </a>
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--warm-gray)' }}>
                  <span>UID: CHE-231.098.289</span>
                </li>
                <li className="mt-3">
                  <a 
                    href="https://www.linkedin.com/in/milla-yesenina-wengi/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                  >
                    <Linkedin className="w-5 h-5" style={{ color: 'var(--warm-sage)' }} />
                  </a>
                </li>
              </ul>

            </div>
          </div>

          {/* Google Maps Embed */}
          <div className="mt-8 flex justify-center">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4487.012198973282!2d8.479805277409048!3d47.191707616534394!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479001e9010116b9%3A0xe8c7b6aa1723ab30!2sLove%20Luxe!5e1!3m2!1sde!2sch!4v1766949488450!5m2!1sde!2sch" 
              width="100%" 
              height="250" 
              style={{ border: 0, borderRadius: '8px', maxWidth: '800px' }} 
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Love Luxe Standort"
            ></iframe>
          </div>

          <Separator style={{ backgroundColor: 'var(--warm-gray)', opacity: 0.3 }} />

          <div className="mt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm" style={{ color: 'var(--warm-gray)' }}>
            <p>© 2025 Love Luxe. {t.footer.allRightsReserved}</p>
            <div className="flex items-center gap-6">
              <button onClick={() => navigate('/privacy-policy')} className="hover:text-white transition-colors">{t.footer.privacyPolicy}</button>
              <button onClick={() => navigate('/terms-of-service')} className="hover:text-white transition-colors">{t.footer.termsOfService}</button>
              <button onClick={() => navigate('/cookie-policy')} className="hover:text-white transition-colors">{t.footer.cookiePolicy}</button>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => {
          setIsContactModalOpen(false);
          setSelectedProduct(null);
        }}
        productName={selectedProduct}
      />

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/+41764150099?text=Hallo%2C%20ich%20interessiere%20mich%20f%C3%BCr%20ein%20Produkt"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
        aria-label="WhatsApp kontaktieren"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="28" 
          height="28" 
          viewBox="0 0 24 24" 
          fill="currentColor"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
};

export default LandingPage;